/**
 * A repeating group's ENTRIES COPIED into the item a rule creates (keepr-api
 * KPR-1054, utils/automations/copyEntries.js): a create-item action gives a
 * group of the card it creates the whole value `{{trigger.<group>}}`, naming a
 * group of the trigger card, and the new item starts with the trigger item's
 * entries. (Not utils/groupCopy.js, which copies a group's DEFINITION onto a
 * card, KPR-859.)
 *
 * The editor's reading only, so it can say before a save which columns copy
 * and which are left out. The API is the authority: it matches the columns by
 * name, leaves out a column the target lacks, a calculated or a file column,
 * and refuses a column of another type when the rule is saved.
 */

import { allowedMeasures, isOwnUnitId, ownUnitOf, sameOwnUnit } from './units';

const COPY_TOKEN = /^\s*\{\{\s*trigger\.([A-Za-z0-9_$-]+)\s*\}\}\s*$/;

const labelOf = el => el?.label?.singular || el?.name || '';
const optionsOf = el => ( el && el.options && typeof el.options === 'object' ? el.options : {} );
const columnsOf = group => ( Array.isArray( optionsOf( group ).columns ) ? optionsOf( group ).columns : [] ).filter( col => col?.name );
const isCalculated = col => !! optionsOf( col ).drivenFrom;

/** The value that copies `group` of the trigger item. */
export const copyTokenFor = group => `{{trigger.${ group }}}`;

/** The trigger group a value copies, or null for any other value. */
export function copySourceOf( value ) {
	if ( typeof value !== 'string' ) return null;
	const m = COPY_TOKEN.exec( value );
	return m ? m[ 1 ] : null;
}

/** Do two columns hold the same kind of value (type, a list or one, the measure)? */
function sameShape( from, to ) {
	if ( from.dataType !== to.dataType ) return false;
	if ( !! optionsOf( from ).allowMultiple !== !! optionsOf( to ).allowMultiple ) return false;
	// A measurement copies into a column that allows every measure it may
	// hold (KPR-1085; one measure each, that is the same measure).
	// An own unit (KPR-1109) only into the same id NAMED alike: a rename
	// keeps an id, so the target column's may be another unit.
	if ( from.dataType === 'measurement' && ! allowedMeasures( optionsOf( from ) ).every( m => allowedMeasures( optionsOf( to ) ).includes( m )
		&& ( ! isOwnUnitId( m ) || sameOwnUnit( ownUnitOf( m, optionsOf( from ) ), ownUnitOf( m, optionsOf( to ) ) ) ) ) ) return false;
	return true;
}

/**
 * planCopy(source, target) -> { copied: [{ column, label }], leftOut: [{ column, label, reason }] }
 *
 * `reason`: 'missing' (the target has no column of that name), 'calculated'
 * (the target works it out), 'file' (a rule attaches no file) or 'type' (the
 * save will refuse it).
 */
export function planCopy( source, target ) {
	const targetCols = new Map( columnsOf( target ).map( col => [ col.name, col ] ) );
	const copied = [];
	const leftOut = [];
	columnsOf( source ).forEach( ( col ) => {
		const to = targetCols.get( col.name );
		const out = reason => leftOut.push( { column: col.name, label: labelOf( col ), reason } );
		if ( ! to ) out( 'missing' );
		else if ( isCalculated( to ) ) out( 'calculated' );
		else if ( col.dataType === 'file' || to.dataType === 'file' ) out( 'file' );
		else if ( ! sameShape( col, to ) ) out( 'type' );
		else copied.push( { column: col.name, label: labelOf( col ) } );
	} );
	return { copied, leftOut };
}

const listWords = ( words ) => {
	if ( words.length <= 1 ) return words[ 0 ] || '';
	if ( words.length === 2 ) return `${ words[ 0 ] } and ${ words[ 1 ] }`;
	return `${ words.slice( 0, -1 ).join( ', ' ) } and ${ words[ words.length - 1 ] }`;
};

const REASON_WORDS = {
	missing: target => `${ target } has no column of that name`,
	calculated: () => 'worked out on each new entry',
	file: () => 'files are not copied',
	type: target => `another type in ${ target }, so the save will refuse it`,
};

/**
 * The sentences under the picker: what copies, and what is left out and why.
 * → { copies, leftOut: [sentence], refused }
 */
export function describeCopy( source, target ) {
	const { copied, leftOut } = planCopy( source, target );
	const targetName = labelOf( target );
	const copies = copied.length
		? `Copies ${ listWords( copied.map( c => c.label ) ) }${ optionsOf( target ).sections ? ', and its headings' : '' }.`
		: `${ labelOf( source ) } and ${ targetName } share no column, so nothing would be copied.`;
	return {
		copies,
		leftOut: leftOut.map( l => `${ l.label }: ${ REASON_WORDS[ l.reason ]( targetName ) }` ),
		refused: ! copied.length || leftOut.some( l => l.reason === 'type' ),
	};
}
